import {BehaviorSubject, merge, Observable} from 'rxjs';

import {debounceTime, map} from 'rxjs/operators';
import {Component, OnInit, ViewChild} from '@angular/core';
import {MatPaginator, PageEvent} from '@angular/material/paginator';
import {MatSort, MatSortable, MatSortHeader} from '@angular/material/sort';
import {DataSource} from '@angular/cdk/collections';
import {Budget} from '../interfaces';
import {DataService} from '../services/data.service';
import {Router} from '@angular/router';
import {ConfirmDialogComponent} from '../private/private.component';
import {MatDialog} from '@angular/material/dialog';
import {MatFormField, MatInput, MatLabel} from '@angular/material/input';
import {
    MatCell,
    MatCellDef, MatColumnDef,
    MatHeaderCell, MatHeaderCellDef,
    MatHeaderRow,
    MatHeaderRowDef,
    MatRow, MatRowDef,
    MatTable
} from '@angular/material/table';
import {MatIcon} from '@angular/material/icon';
import {CurrencyPipe, DatePipe} from '@angular/common';
import {TimestampPipe} from '../shared/pipes/timestamp.pipe';
import {MatIconButton} from '@angular/material/button';



@Component({
    selector: 'app-internal-project-list',
    templateUrl: './internal-project-list.component.html',
    imports: [
        MatFormField,
        MatPaginator,
        MatCell,
        MatHeaderCell,
        MatRowDef,
        MatTable,
        MatHeaderRow,
        MatIcon,
        CurrencyPipe,
        TimestampPipe,
        DatePipe,
        MatCellDef,
        MatHeaderRowDef,
        MatRowDef,
        MatColumnDef,
        MatColumnDef,
        MatSort,
        MatSortHeader,
        MatIconButton,
        MatHeaderCellDef,
        MatHeaderCellDef,
        MatInput,
        MatRow,
        MatLabel
    ],
    styleUrls: ['./internal-project-list.component.scss']
})
export class InternalProjectListComponent implements OnInit {
    displayedColumns = ['projectNr', 'budgetNr', 'description', 'total', 'final', 'date', 'archivedDate', 'actions'];
    dataSource: BudgetDataSource;

    @ViewChild(MatPaginator, {static: true}) paginator: MatPaginator;
    @ViewChild(MatSort, {static: true}) sort: MatSort;

    initialFilterValue: string;

    constructor(private dataService: DataService,
                public dialog: MatDialog,
                private router: Router) {
    }


    ngOnInit() {

        const budgetStream$ = this.dataService.getInternalProjects();
        // Assign the data to the data source for the table to render
        this.dataSource = new BudgetDataSource(
            budgetStream$,
            this.paginator, this.sort);

        this.initialFilterValue = localStorage.getItem('InternalProjectListFilter') || '';
        this.dataSource.filter = this.initialFilterValue;
    }

    public deleteProject(event: Event, id: string) {
        event.stopPropagation();
        const dialogMessage = {confirmMessage: `Are you sure you want to delete the selected budget?`};
        const dialogData = {data: dialogMessage, height: '250px', width: '600px', disableClose: true};
        const dialogRef = this.dialog.open(ConfirmDialogComponent, dialogData);

        dialogRef.afterClosed().subscribe(async choice => {
            if (choice.confirm) {
                this.dataService.deleteBudget(id).subscribe(() => {
                    console.log('Budget deleted');
                });
            }
        });

    }

    applyFilter(event: Event) {
        const filterValue = (event.target as HTMLInputElement).value;
        const trimmedValue = filterValue.trim().toLowerCase();
        this.dataSource.filter = trimmedValue;
        localStorage.setItem('InternalProjectListFilter', trimmedValue);
    }

    clearFilter(inputElement: HTMLInputElement) {
        inputElement.value = '';
        this.dataSource.filter = '';
        localStorage.setItem('InternalProjectListFilter', '');
    }

    public goToBudget(row: Budget) {
        const dest = '/budget/' + row.id;
        this.router.navigate([dest]);
    }

}


// Thanks to:
// https://stackoverflow.com/questions/47230980/angular-material-data-table-with-filtering-using-firestore

/**
 * Data source to provide what data should be rendered in the table. Note that the data source
 * can retrieve its data in any way. In this case, the data source is provided a reference
 * to a common data base, ExampleDatabase. It is not the data source's responsibility to manage
 * the underlying data. Instead, it only needs to take the data and send the table exactly what
 * should be rendered.
 */
class BudgetDataSource extends DataSource<Budget> {

    _filterChange = new BehaviorSubject('');
    _dataChange = new BehaviorSubject([]);

    get filter(): string {
        return this._filterChange.value;
    }

    set filter(filter: string) {
        this._filterChange.next(filter);
    }

    // The number of issues returned by github matching the query.
    public resultsLength = 0;
    public isLoadingResults = false;

    constructor(private observable,
                private _paginator: MatPaginator,
                private _sort: MatSort) {
        super();
        this.observable.subscribe(this._dataChange);
    }

    /** Connect function called by the table to retrieve one stream containing the data to render. */
    connect(): Observable<Budget[]> {
        // Listen for any changes in the base data, sorting, filtering, or pagination
        const displayDataChanges = [
            this.observable,
            this._sort.sortChange,
            this._filterChange.pipe(debounceTime(500)),
            this._paginator.page
        ];

        this._paginator._changePageSize(Number(localStorage.getItem('internalProjectListPageSize')));
        this._paginator.page.next(new PageEvent());
        this._paginator.page.subscribe(paginationData => {
            localStorage.setItem('internalProjectListPageSize', paginationData.pageSize.toString());
        });

        const sortId = localStorage.getItem('internalProjectListSortId');
        const sortDirection = localStorage.getItem('internalProjectListSortDirection');

        this._sort.sort(({id: sortId, start: sortDirection}) as MatSortable);

        return merge(...displayDataChanges).pipe(map(() => {
            // console.log('Combine ', data, filter, page);
            const data = this._dataChange.value;
            // Filter
            const fData = data.filter((item: Budget) => {
                let searchStr = (item.budgetNr || '') +
                    (item.budgetName || '') +
                    (item.description || '') +
                    (item.author?.displayName || '');
                if (item.project) {
                    searchStr += (item.project.projectNr || '');
                }
                if (item.tags && item.tags.length > 0) {
                    searchStr += item.tags.join(' ');
                }
                searchStr = searchStr.toLowerCase();
                return searchStr.includes(this.filter.toLowerCase());
            });

            this.resultsLength = fData.length;
            const startIndex = this._paginator.pageIndex * this._paginator.pageSize;
            this.sortData(fData);
            return fData.splice(startIndex, this._paginator.pageSize);
        }));

    }

    disconnect() {
    }

    private sortData = (budget: Budget[]) => {
        console.log('sort ', this._sort.active, this._sort.direction);

        localStorage.setItem('internalProjectListSortId', this._sort.active);
        localStorage.setItem('internalProjectListSortDirection', this._sort.direction);

        if (!this._sort.active || !this._sort.direction) {
            return;
        }

        budget.sort((a, b) => {
            const isAsc = this._sort.direction === 'asc';
            let propertyA: number | string | Date | null | undefined;
            let propertyB: number | string | Date | null | undefined;

            switch (this._sort.active) {
                case 'projectNr':
                    propertyA = a.project?.projectNr;
                    propertyB = b.project?.projectNr;
                    break;
                case 'total':
                    propertyA = (a.project as any)?.cost;
                    propertyB = (b.project as any)?.cost;
                    break;
                case 'budgetNr':
                    propertyA = a.budgetNr;
                    propertyB = b.budgetNr;
                    break;
                case 'description':
                    propertyA = a.description;
                    propertyB = b.description;
                    break;
                case 'category':
                    propertyA = a.category;
                    propertyB = b.category;
                    break;
                case 'date':
                case 'creation':
                    propertyA = a.creation;
                    propertyB = b.creation;
                    break;
                case 'archivedDate':
                    propertyA = a.archivedDate;
                    propertyB = b.archivedDate;
                    break;
                default:
                    return 0;
            }

            return this.compare(propertyA, propertyB, isAsc);
        });
    }

    private compare(a: any, b: any, isAsc: boolean): number {
        // Handle nulls and undefined
        if (a == null && b == null) {
            return 0;
        }
        if (a == null) {
            return isAsc ? -1 : 1;
        }
        if (b == null) {
            return isAsc ? 1 : -1;
        }

        // Handle Firestore Timestamps
        if (typeof a.toDate === 'function') {
            a = a.toDate();
        }
        if (typeof b.toDate === 'function') {
            b = b.toDate();
        }

        // Handle dates
        if (a instanceof Date && b instanceof Date) {
            return (a.getTime() - b.getTime()) * (isAsc ? 1 : -1);
        }

        // Handle strings, with case-insensitive comparison
        if (typeof a === 'string' && typeof b === 'string') {
            const lowerA = a.toLowerCase();
            const lowerB = b.toLowerCase();
            if (lowerA < lowerB) {
                return isAsc ? -1 : 1;
            }
            if (lowerA > lowerB) {
                return isAsc ? 1 : -1;
            }
            return 0;
        }

        // Handle numbers
        if (typeof a === 'number' && typeof b === 'number') {
            return (a - b) * (isAsc ? 1 : -1);
        }

        // Fallback for mixed types (e.g. string and number)
        // Convert to string for a consistent, if not perfect, comparison
        const stringA = String(a).toLowerCase();
        const stringB = String(b).toLowerCase();

        if (stringA < stringB) {
            return isAsc ? -1 : 1;
        }
        if (stringA > stringB) {
            return isAsc ? 1 : -1;
        }

        return 0;
    }
}